import React, { memo, useCallback } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, number, shape, string,
} from 'prop-types'

import { setAspectRatio, setModelId, setVariations } from 'Pods/ThreeDStudio/Store/actions/imageSettings'

import GenerationSettings from '../GenerationSettings/GenerationSettings'
import ModelSelector from '../ModelSelector/ModelSelector'
import PrivateToggle from '../PrivateToggle/PrivateToggle'
import StyleButton from '../StyleButton/StyleButton'

import useCollapsedControls from './useCollapsedControls'

// The settings popover reports a change by setting key; each key has its own store action.
const SETTING_ACTIONS = {
  aspectRatio: setAspectRatio,
  variations: setVariations,
}

function ComposerControls({ imageSettings, hasStyleButton, isFullWidth = false }) {
  const handleSettingsChange = useCallback((key, value) => {
    SETTING_ACTIONS[key]?.(value)
  }, [])

  // The Image Generator page centres the composer with empty space below it, so its popovers
  // open downwards instead of covering the heading; floating-ui flips them back up when the
  // composer sits at the bottom of a conversation.
  const modelPlacement = isFullWidth ? 'bottom-start' : undefined
  const settingsPlacement = isFullWidth ? 'bottom' : undefined

  // Model, Style and Private. When the row runs out of room they move into the settings
  // popover from the end, so Private collapses first and the model selector last.
  const buildControls = (isStacked) => [
    <ModelSelector
      key="model"
      value={imageSettings.modelId}
      onChange={setModelId}
      isStacked={isStacked}
      placement={modelPlacement}
    />,
    hasStyleButton && <StyleButton key="style" isStacked={isStacked} />,
    <PrivateToggle key="private" isStacked={isStacked} />,
  ].filter(Boolean)
  const rowControls = buildControls(false)
  const { visibleCount, isMeasured, controlRefs } = useCollapsedControls(rowControls.length)
  const stackedControls = buildControls(true).slice(visibleCount)

  // Collapsed controls stay in the row as hidden, inert copies so their widths can
  // still be measured to decide when they fit again. Until the first measurement every
  // control but the first is pending: the server HTML cannot know the row's width, so
  // below 1280px, where the row runs out of room, those wait out of flow rather than
  // overflow the composer.
  return (
    <>
      {rowControls.map((control, index) => {
        const isCollapsed = index >= visibleCount
        return (
          <div
            key={control.key}
            ref={controlRefs[index]}
            className={classnames(
              'image-assistant-create-tab__control',
              isCollapsed && 'image-assistant-create-tab__control--measure-only',
              !isMeasured && index > 0 && 'image-assistant-create-tab__control--pending',
            )}
            aria-hidden={isCollapsed || undefined}
            inert={isCollapsed ? '' : undefined}
          >
            {control}
          </div>
        )
      })}
      <GenerationSettings
        settings={imageSettings}
        stackedControls={stackedControls.length > 0 ? stackedControls : null}
        onChange={handleSettingsChange}
        placement={settingsPlacement}
      />
    </>
  )
}

ComposerControls.propTypes = {
  imageSettings: shape({
    modelId: string.isRequired,
    aspectRatio: string.isRequired,
    variations: number.isRequired,
  }).isRequired,
  hasStyleButton: bool.isRequired,
  isFullWidth: bool,
}

export default memo(ComposerControls)
